/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  effect,
  forwardRef,
  HostListener,
  input,
  signal,
  output,
  inject,
} from '@angular/core';
import type { ComboboxOption } from './combobox.interfaces';
import { IconDirective } from '@ui/components/icon';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'ui-combobox',
  imports: [IconDirective],
  templateUrl: './combobox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ComboboxComponent),
      multi: true,
    },
  ],
})
export class ComboboxComponent implements ControlValueAccessor {
  /**
   * Anatomy of combobox
   *
   * Container (relative)
   *   - Label (absolute) (optional)
   *   - Button
   *   - Menu (absolute)
   *     - Input
   *     - Options
   *       - Option
   *       - No results
   */

  private elementRef = inject(ElementRef);

  constructor() {
    effect(() => {
      const options = this.options();
      if (options && this.pendingValue) {
        this.writeValue(this.pendingValue);
        this.pendingValue = null;
      }
    });
  }

  private pendingValue: string | null = null;

  size = input<'sm' | 'md'>('sm');
  disabled = input(false); // from input binding
  _disabledByCVA = signal(false); // from ControlValueAccessor
  isDisabled = computed(() => this.disabled() || this._disabledByCVA());

  label = input<string | null>(null);
  placeholder = input<string>('select an option');

  options = input.required<ComboboxOption[]>();
  filteredOptions = computed(() =>
    this.options().filter((option) =>
      option?.value?.toLowerCase().includes(this.search().toLowerCase()),
    ),
  );

  search = signal<string>('');

  open = signal<boolean>(false);
  toggleCombobox = () => {
    this.open.set(!this.open());
    if (this.open()) {
      this.focusedOptionIndex.set(-1);
      // Focus the search input when opening the combobox
      setTimeout(() => {
        const searchInput = this.elementRef.nativeElement.querySelector(
          'input[role="searchbox"]',
        );
        if (searchInput) {
          searchInput.focus();
        }
      }, 0);
    }
  };

  selectedOption = signal<ComboboxOption | null>(null);
  focusedOptionIndex = signal<number>(-1);

  selected = output<ComboboxOption | null>();

  selectOption(option: ComboboxOption) {
    if (this.isDisabled()) return;
    if (this.selectedOption() === option) {
      this.selectedOption.set(null);
      this.onChange(null);
      this.selected.emit(null);
    } else {
      this.selectedOption.set(option);
      this.onChange(option.id);
      this.selected.emit(option);
    }
    this.onTouched();
    this.search.set('');
    this.open.set(false);
    this.focusedOptionIndex.set(-1);
  }

  // ControlValueAccessor implementation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  onChange = (value: string | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  writeValue(value: string | null): void {
    if (!value) {
      this.selectedOption.set(null);
      this.pendingValue = null;
      return;
    }

    const options = this.options();
    if (options && options.length > 0) {
      const option = options.find((o) => o.id === value);
      this.selectedOption.set(option ?? null);
      this.pendingValue = null;
    } else {
      this.pendingValue = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this._disabledByCVA.set(isDisabled);
  }

  get iconSize(): number {
    return this.size() === 'sm' ? 10 : 12;
  }

  get labelClasses(): string {
    const base =
      'absolute text-xs font-medium -top-2.5 left-3 z-10 bg-white px-1 rounded-md group-hover:bg-foreground-5 transition duration-300 text-neutral-700 ';
    const sizes: Record<string, string> = {
      sm: 'text-xs',
      md: 'text-base',
    };
    const sizeClass = sizes[this.size()];
    return `${base} ${sizeClass}`;
  }

  get comboboxButtonClasses(): string {
    const base =
      'w-full rounded-lg border border-neutral-700 group-hover:bg-foreground-5 transition duration-300 text-left';
    const sizes: Record<string, string> = {
      sm: 'px-2 py-2 text-sm',
      md: 'px-4 py-2 text-base',
    };
    const disabledClass = this.isDisabled()
      ? 'opacity-50 cursor-not-allowed hover:bg-transparent'
      : '';
    const sizeClass = sizes[this.size()];
    return `${base} ${sizeClass} ${disabledClass}`;
  }

  get comboboxMenuClasses(): string {
    const base = 'bg-white border border-foreground/20 rounded-lg shadow-sm';
    const sizes: Record<string, string> = {
      sm: 'p-0 text-sm',
      md: 'p-0 text-base',
    };
    const sizeClass = sizes[this.size()];
    return `${base} ${sizeClass}`;
  }

  get comboboxInputClasses(): string {
    const base =
      'w-full py-2 pl-3 pr-10 border-b border-foreground/20 focus:outline-none focus:ring-0';
    const sizes: Record<string, string> = {
      sm: 'text-sm',
      md: 'text-base',
    };
    const sizeClass = sizes[this.size()];
    return `${base} ${sizeClass}`;
  }

  get comboboxOptionClasses(): string {
    const base = 'px-4 py-2 hover:bg-foreground-5 cursor-pointer';
    const sizes: Record<string, string> = {
      sm: 'text-sm',
      md: 'text-base',
    };
    const sizeClass = sizes[this.size()];
    return `${base} ${sizeClass}`;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.open.set(false);
    }
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (!this.open()) return;

    const options = this.filteredOptions();
    if (options.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedOptionIndex.update((index) => {
          const newIndex = index < options.length - 1 ? index + 1 : 0;
          return newIndex;
        });
        this.focusOptionAtIndex(this.focusedOptionIndex());
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusedOptionIndex.update((index) => {
          const newIndex = index > 0 ? index - 1 : options.length - 1;
          return newIndex;
        });
        this.focusOptionAtIndex(this.focusedOptionIndex());
        break;
      case 'Enter': {
        event.preventDefault();

        const focusedIndex = this.focusedOptionIndex();

        if (focusedIndex >= 0 && focusedIndex < options.length) {
          this.selectOption(options[focusedIndex]);
          return;
        }

        if (event.target instanceof HTMLElement) {
          const liElement = event.target.closest('li');
          if (liElement) {
            const optionId = liElement.id?.replace('option-', '');
            if (optionId) {
              const option = options.find((o) => o.id === optionId);
              if (option) {
                this.selectOption(option);
                return;
              }
            }

            const allListItems = Array.from(
              this.elementRef.nativeElement.querySelectorAll(
                'li[role="option"]',
              ),
            );
            const lastIndex = allListItems.length - 1;

            if (
              lastIndex >= 0 &&
              allListItems.indexOf(liElement) === lastIndex &&
              options.length > 0
            ) {
              this.selectOption(options[options.length - 1]);
              return;
            }
          }
        }

        if (options.length > 0) {
          this.selectOption(options[0]);
        }

        break;
      }
      case 'Escape':
        event.preventDefault();
        this.open.set(false);
        this.focusedOptionIndex.set(-1);
        break;
      // No need to handle Tab specifically anymore since we have focus events
      // Just let the browser handle tab navigation naturally
    }
  }

  focusOptionAtIndex(index: number): void {
    if (index < 0) return;

    const options = this.filteredOptions();
    if (index >= options.length) return;

    setTimeout(() => {
      const optionElement = this.elementRef.nativeElement.querySelector(
        `#option-${options[index].id}`,
      );
      if (optionElement) {
        optionElement.focus();
      }
    }, 0);
  }

  handleOptionFocus(option: ComboboxOption): void {
    const options = this.filteredOptions();
    const index = options.findIndex((o) => o.index === option.index);
    if (index >= 0) {
      this.focusedOptionIndex.set(index);
    }
  }
}
